import {
	Box,
	Button,
	Step,
	StepConnector,
	stepConnectorClasses,
	StepIconProps,
	StepLabel,
	Stepper,
	styled,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Check } from '@mui/icons-material';

interface IStep {
	stepName: string;
	stepDetails: React.ReactNode;
	nextButton?: React.ReactNode;
}

interface IProps {
	steps: IStep[];
	onStepChange?: (stepNumber: number) => void;
	stepOverride?: number;
}

const BasicStepConnector = styled(StepConnector)(({ theme }) => ({
	[`&.${stepConnectorClasses.alternativeLabel}`]: {
		top: 25,
		left: 'calc(-50% + 23px)',
		right: 'calc(50% + 23px)',
	},
	[`&.${stepConnectorClasses.active}`]: {
		[`& .${stepConnectorClasses.line}`]: {
			borderColor: theme.palette.secondary.main,
		},
	},
	[`&.${stepConnectorClasses.completed}`]: {
		[`& .${stepConnectorClasses.line}`]: {
			borderColor: theme.palette.secondary.main,
		},
	},
	[`& .${stepConnectorClasses.line}`]: {
		borderColor: theme.palette.grey[200],
		borderTopWidth: 3,
		borderRadius: 1,
	},
}));
const StepIconRoot = styled('div')<{
	ownerState: { active?: boolean; completed?: boolean };
}>(({ theme, ownerState }) => ({
	border: `3px solid ${theme.palette.grey[200]}`,
	color: '#eaeaf0',
	zIndex: 1,
	width: 50,
	height: 50,
	display: 'flex',
	borderRadius: '50%',
	justifyContent: 'center',
	alignItems: 'center',
	fontSize: 25,
	fontWeight: 'bold',
	...(ownerState.active && {
		color: theme.palette.secondary.main,
		border: `3px solid ${theme.palette.secondary.main}`,
	}),
	...(ownerState.completed && {
		border: `3px solid ${theme.palette.secondary.main}`,
		backgroundColor: theme.palette.secondary.main,
	}),
	'& .QontoStepIcon-completedIcon': {
		color: theme.palette.common.white,
		zIndex: 1,
		fontSize: 40,
		fontWeight: 'bold',
	},
	'& .QontoStepIcon-circle': {
		width: 8,
		height: 8,
		borderRadius: '50%',
		backgroundColor: 'currentColor',
	},
}));

const StepIcon = (props: StepIconProps) => {
	const { active, completed, className, icon } = props;

	return (
		<StepIconRoot ownerState={{ active, completed }} className={className}>
			{completed ? <Check className='QontoStepIcon-completedIcon' /> : <div>{icon}</div>}
		</StepIconRoot>
	);
};

const BasicStepper: FC<IProps> = ({ steps, onStepChange, stepOverride }: IProps) => {
	const [activeStep, setActiveStep] = useState(0);

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
		if (onStepChange) onStepChange(activeStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
		if (onStepChange) onStepChange(activeStep - 1);
	};

	useEffect(() => {
		if (stepOverride) setActiveStep(stepOverride);
	}, [stepOverride]);

	return (
		<Box sx={{ width: '100%' }}>
			<Stepper
				alternativeLabel
				activeStep={activeStep}
				sx={(theme) => ({
					mx: '2rem',
					my: 3,
					[theme.breakpoints.down('sm')]: { mx: 0 },
				})}
				connector={<BasicStepConnector />}
			>
				{steps.map((step) => {
					const stepProps: { completed?: boolean } = {};
					const labelProps: {
						optional?: React.ReactNode;
					} = {};
					return (
						<Step key={step.stepName} {...stepProps}>
							<StepLabel
								slots={{
									stepIcon: StepIcon,
								}}
								{...labelProps}
							>
								{step.stepName}
							</StepLabel>
						</Step>
					);
				})}
			</Stepper>
			<Box>{steps.filter((_v, i) => i === activeStep).map((step) => step.stepDetails)}</Box>
			<Box
				sx={(theme) => ({
					display: 'flex',
					flexDirection: 'row',
					pt: 2,
					mx: 4,
					[theme.breakpoints.down('sm')]: { mx: 0 },
				})}
			>
				{activeStep !== 0 && (
					<Button
						color='inherit'
						onClick={handleBack}
						sx={{ mr: 1, minWidth: '150px' }}
						variant='text'
						startIcon={<ArrowBackIcon />}
					>
						Back
					</Button>
				)}

				{steps.filter((_v, i) => i === activeStep)[0] && steps.filter((_v, i) => i === activeStep)[0].nextButton ? (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						{steps.filter((_v, i) => i === activeStep)[0].nextButton}
					</Box>
				) : (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						<Button
							onClick={handleNext}
							variant='contained'
							color='secondary'
							size='large'
							endIcon={<ArrowForwardIcon />}
							sx={(theme) => ({
								marginRight: 'auto',
								minWidth: '150px',
								marginLeft: 'auto',
								[theme.breakpoints.up('sm')]: { marginRight: 0 },
							})}
						>
							{activeStep === steps.length - 1 ? 'Submit' : 'Next'}
						</Button>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default BasicStepper;
